<template>
  <div>
    <div class="btn-group segmented-control m-b-sm" role="group" aria-label="Schedule kind">
      <button type="button" class="btn btn-default btn-compact" :class="{ active: !allDayLocal }"
              @click="allDayLocal = false" data-cy="wizard-timed-tab">Specific times</button>
      <button type="button" class="btn btn-default btn-compact" :class="{ active: allDayLocal }"
              @click="allDayLocal = true" data-cy="wizard-all-day-tab">All day</button>
    </div>
    <!-- Common timed case is a single sentence-like row: date · start – end.
         The end date only exists for all-day spans or explicitly-revealed
         overnight events; otherwise it silently tracks the start date so it
         can't be misread as "when the repeating schedule ends" -->
    <div class="grid gap-x-xs gap-y-xs" :class="(allDay || showEndDate) ? 'grid-cols-2' : 'grid-cols-3 sm-grid-cols-2'">
      <!-- On phones the 3-up row is too tight for a full date, so the date takes
           its own row and the times pair up below it -->
      <div class="flex flex-col gap-y-xxs" :class="(allDay || showEndDate) ? '' : 'sm-col-span-2'">
        <label class="text-12 m-y-none tracking-wide">Start date</label>
        <input type="date" v-model="startDate" class="form-control" data-cy="wizard-start-date">
      </div>
      <div v-show="!allDay" class="flex flex-col gap-y-xxs">
        <label class="text-12 m-y-none tracking-wide">Start time</label>
        <time-select v-model="startTime" data-cy="wizard-start-time"></time-select>
      </div>
      <div v-if="allDay || showEndDate" class="flex flex-col gap-y-xxs">
        <label class="text-12 m-y-none tracking-wide">End date</label>
        <input type="date" v-model="endDate" class="form-control" data-cy="wizard-end-date">
      </div>
      <div v-show="!allDay" class="flex flex-col gap-y-xxs">
        <label class="text-12 m-y-none tracking-wide">End time</label>
        <time-select v-model="endTime" :base-time="sameDay ? startTime : null" data-cy="wizard-end-time"></time-select>
      </div>
    </div>
    <button v-if="!allDay && !showEndDate" type="button"
            class="btn btn-link p-x-n text-12 text-gray-500 m-t-xxs" data-cy="wizard-reveal-end-date"
            @click="showEndDate = true">Ends on a different day?</button>
    <p v-if="durationLabel" class="text-14 m-t-xs" :class="durationSuspicious ? 'text-yellow-700 font-semi' : 'text-gray-500'" data-cy="wizard-event-length">
      Event length: {{ durationLabel }}<template v-if="durationSuspicious"> — is that right? For a repeating schedule, use Repeats below.</template>
    </p>
    <p v-if="rangeInvalid" class="text-14 text-red-600 m-t-xs">
      End must be after the start.<template v-if="!allDay && !showEndDate"> Runs past midnight? Use "Ends on a different day?".</template>
    </p>
  </div>
</template>

<script>
import TimeSelect from './time_select.vue';
import { formTimeZone } from '../event_time_zone';

// Native date inputs + 15-minute-increment time comboboxes replacing the
// jQuery datepicker + timepicker + Datepair stack on the event wizard.
// Reimplements Datepair's one useful behavior the way calendar apps do it:
// moving the start shifts the end to preserve the current duration; moving the
// end just changes the duration.
// Emits `change` with { start, end, allDay, valid } where start/end are moment
// objects (all-day end is exclusive, i.e. +1 day, matching FullCalendar).
// Timed values are read on `timeZone`'s clock (the event's override or its
// location's zone), falling back to the browser's. Changing the zone keeps the
// typed wall-clock times: "6pm" then picking the Eastern gym means 6pm Eastern.
export default {
  name: 'date-time-range',
  components: {
    TimeSelect,
  },
  props: {
    initialStartDate: { type: String, required: false, default: '' }, // YYYY-MM-DD
    initialStartTime: { type: String, required: false, default: '' }, // HH:mm
    initialEndDate: { type: String, required: false, default: '' },
    initialEndTime: { type: String, required: false, default: '' },
    initialAllDay: { type: Boolean, required: false, default: false },
    // The fresh-build flow defaults blank dates to today; the template flow
    // passes false so a reused event forces an active date choice
    defaultToToday: { type: Boolean, required: false, default: true },
    // IANA zone the event is on (override or location's); null = browser zone
    timeZone: { type: String, required: false, default: null },
  },
  data() {
    const startDate = this.initialStartDate || (this.defaultToToday ? moment().format('YYYY-MM-DD') : '');
    const endDate = this.initialEndDate || startDate;
    return {
      startDate,
      startTime: this.initialStartTime,
      endDate,
      endTime: this.initialEndTime,
      allDayLocal: this.initialAllDay,
      // The timed-mode end date stays hidden (tracking the start date) unless
      // the event genuinely spans days — prefilled or user-revealed
      showEndDate: endDate !== startDate,
      // Suppresses the duration-preserving watchers while we programmatically
      // move the end fields ourselves.
      syncing: false,
    };
  },
  computed: {
    allDay() {
      return this.allDayLocal;
    },
    // Duration hints on the end-time list only make sense within a single day;
    // overnight/multi-day events pick times from the plain full-day list.
    sameDay() {
      return this.startDate === this.endDate;
    },
    start() {
      const vm = this;
      if (!vm.startDate) {
        return null;
      }
      if (vm.allDay) {
        // All-day events are time ambiguous, no timezone conversion
        return moment(vm.startDate, 'YYYY-MM-DD');
      }
      if (!vm.startTime) {
        return null;
      }
      return moment.tz(`${vm.startDate} ${vm.startTime}`, 'YYYY-MM-DD HH:mm', formTimeZone(vm.timeZone));
    },
    end() {
      const vm = this;
      if (!vm.endDate) {
        return null;
      }
      if (vm.allDay) {
        // Exclusive end: "all day 2/7 to 2/7" is really midnight 2/7 to midnight
        // 2/8, matching how FullCalendar and the API treat all-day spans.
        return moment(vm.endDate, 'YYYY-MM-DD').add(1, 'day');
      }
      if (!vm.endTime) {
        return null;
      }
      return moment.tz(`${vm.endDate} ${vm.endTime}`, 'YYYY-MM-DD HH:mm', formTimeZone(vm.timeZone));
    },
    rangeInvalid() {
      return !!(this.start && this.end && !this.end.isAfter(this.start));
    },
    // Human-readable event length ("1 hr 30 mins", "3 days") — a second way to
    // notice the classic mistake of typing the series-end date into End date,
    // which reads back as a multi-day event length.
    durationLabel() {
      const vm = this;
      if (!vm.start || !vm.end || vm.rangeInvalid) {
        return null;
      }
      if (vm.allDay) {
        const days = vm.end.diff(vm.start, 'days');
        return `${days} day${days === 1 ? '' : 's'}`;
      }
      const totalMinutes = vm.end.diff(vm.start, 'minutes');
      const days = Math.floor(totalMinutes / (24 * 60));
      const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
      const minutes = totalMinutes % 60;
      const parts = [];
      if (days > 0) {
        parts.push(`${days} day${days === 1 ? '' : 's'}`);
      }
      if (hours > 0) {
        parts.push(`${hours} hr${hours === 1 ? '' : 's'}`);
      }
      if (minutes > 0 || parts.length === 0) {
        parts.push(`${minutes} min${minutes === 1 ? '' : 's'}`);
      }
      return parts.join(' ');
    },
    // A timed event spanning a full day or more is almost always the
    // series-end-date confusion
    durationSuspicious() {
      if (this.allDay || !this.start || !this.end || this.rangeInvalid) {
        return false;
      }
      return this.end.diff(this.start, 'minutes') >= 24 * 60;
    },
  },
  created() {
    this.emitChange();
  },
  watch: {
    startDate(newVal, oldVal) {
      // The timed-mode end date field is hidden until revealed, so when it's
      // still blank (template flow mounts with both dates empty) nothing else
      // can ever set it — snap it to the start so the range can become valid.
      if (!this.syncing && newVal && !this.endDate && !this.showEndDate && !this.allDay) {
        this.syncing = true;
        this.endDate = newVal;
        this.$nextTick(() => { this.syncing = false; });
        this.emitChange();
        return;
      }
      if (this.syncing || !newVal || !oldVal || !this.endDate) {
        this.emitChange();
        return;
      }
      // Shift the end date to preserve the day span
      const dayDelta = moment(newVal, 'YYYY-MM-DD').diff(moment(oldVal, 'YYYY-MM-DD'), 'days');
      if (dayDelta !== 0) {
        this.syncing = true;
        this.endDate = moment(this.endDate, 'YYYY-MM-DD').add(dayDelta, 'days').format('YYYY-MM-DD');
        this.$nextTick(() => { this.syncing = false; });
      }
      this.emitChange();
    },
    startTime(newVal, oldVal) {
      if (this.syncing || !newVal || !oldVal || !this.endTime) {
        this.emitChange();
        return;
      }
      // Shift the end time to preserve the duration, rolling the end date
      // forward/back if the shift crosses midnight.
      const minuteDelta = moment(newVal, 'HH:mm').diff(moment(oldVal, 'HH:mm'), 'minutes');
      if (minuteDelta !== 0) {
        this.syncing = true;
        const newEnd = moment(`${this.endDate} ${this.endTime}`, 'YYYY-MM-DD HH:mm').add(minuteDelta, 'minutes');
        this.endTime = newEnd.format('HH:mm');
        this.endDate = newEnd.format('YYYY-MM-DD');
        this.$nextTick(() => { this.syncing = false; });
      }
      this.emitChange();
    },
    endDate() {
      this.emitChange();
    },
    endTime() {
      this.emitChange();
    },
    timeZone() {
      this.emitChange();
    },
    allDayLocal(nowAllDay) {
      // Coming back from an all-day multi-day span: keep the end date visible
      // rather than silently hiding a date that no longer matches the start
      if (!nowAllDay && this.endDate !== this.startDate) {
        this.showEndDate = true;
      }
      this.emitChange();
    },
  },
  methods: {
    emitChange() {
      this.$emit('change', {
        start: this.start,
        end: this.end,
        allDay: this.allDay,
        valid: !!(this.start && this.end && !this.rangeInvalid),
      });
    },
  },
};
</script>
